/**
 * 백오피스 MUI 테마 — Monolith 콜드 라이트.
 *
 * MainLayout · FullModalLayout · Login 세 곳에서 감싼다. App.tsx 의 전역 테마(blue/deepPurple)는
 * 홈페이지 쪽 결제 컴포넌트가 아직 참조하므로 손대지 않고, 백오피스 트리 안에서만 덮는다.
 *
 * 규칙(DESIGN_SYSTEM §2·§5): 액센트 0 — primary 가 곧 ink. 그림자 없음, radius 2, 대문자 변환 없음.
 */
import React from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { koKR } from '@mui/x-data-grid/locales'
import { koKR as pickersKoKR } from '@mui/x-date-pickers/locales'
import { koKR as coreKoKR } from '@mui/material/locale'

import { useAppSelector } from '@redux/index'
import { mono, boFont, boSize } from './backoffice.tokens'

const buildTheme = (language: string) => {
	const base = {
		palette: {
			mode: 'light' as const,
			primary: {
				main: mono.ink,
				dark: mono.ctaBg,
				light: mono.dim,
				contrastText: '#FFFFFF',
			},
			secondary: { main: mono.dim, contrastText: '#FFFFFF' },
			error: { main: mono.error },
			warning: { main: mono.dim },
			info: { main: mono.dim },
			success: { main: mono.ink },
			text: { primary: mono.ink, secondary: mono.dim, disabled: mono.faint },
			divider: mono.line,
			background: { default: mono.bg, paper: mono.surface },
			action: {
				hover: 'rgba(22, 24, 27, 0.04)',
				selected: 'rgba(22, 24, 27, 0.06)',
				focus: 'rgba(22, 24, 27, 0.08)',
				disabledBackground: mono.line,
			},
		},
		shape: { borderRadius: 2 },
		typography: {
			fontFamily: boFont.ui,
			fontSize: 13,
			button: { textTransform: 'none' as const, fontWeight: 500 },
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: { body: { fontFamily: boFont.ui } },
			},
			MuiButtonBase: {
				defaultProps: { disableRipple: true },
			},
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						minHeight: boSize.ctlH,
						padding: '0 16px',
						fontSize: 13,
						fontWeight: 500,
						lineHeight: 1,
						borderRadius: 2,
						boxShadow: 'none',
						transition:
							'color 200ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out',
						'&:hover': { boxShadow: 'none' },
					},
					sizeSmall: { minHeight: 30, padding: '0 12px', fontSize: 12 },
					contained: {
						background: mono.ctaBg,
						color: '#FFFFFF',
						'&:hover': { background: mono.ink },
					},
					containedInherit: {
						background: 'transparent',
						color: mono.ink,
						border: `1px solid ${mono.ink}`,
						'&:hover': { background: mono.ink, color: '#FFFFFF' },
					},
					outlined: {
						borderColor: mono.ink,
						color: mono.ink,
						'&:hover': { background: mono.ink, color: '#FFFFFF', borderColor: mono.ink },
					},
					text: {
						color: mono.dim,
						'&:hover': { color: mono.ink, background: 'transparent' },
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						borderRadius: 2,
						color: mono.dim,
						'&:hover': { color: mono.ink, background: 'rgba(22, 24, 27, 0.04)' },
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: 2,
						background: mono.surface,
						fontSize: 13,
						'& .MuiOutlinedInput-notchedOutline': { borderColor: mono.lineInput },
						'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: mono.dim },
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: mono.ink,
							borderWidth: 1,
						},
					},
					input: { padding: '9px 12px' },
					inputSizeSmall: { padding: '7px 10px' },
				},
			},
			MuiFilledInput: {
				styleOverrides: {
					root: {
						borderRadius: 0,
						background: mono.bg,
						'&:before': { borderBottomColor: mono.lineInput },
						'&:after': { borderBottomColor: mono.ink },
						'&:hover': { background: mono.bg },
						'&.Mui-focused': { background: mono.bg },
					},
				},
			},
			MuiInput: {
				styleOverrides: {
					root: {
						fontSize: 14,
						fontWeight: 300,
						'&:before': { borderBottomColor: mono.lineInput },
						'&:hover:not(.Mui-disabled, .Mui-error):before': {
							borderBottomColor: mono.dim,
						},
						'&:after': { borderBottomColor: mono.ink },
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						fontSize: 13,
						color: mono.faint,
						'&.Mui-focused': { color: mono.ink },
					},
				},
			},
			MuiFormHelperText: {
				styleOverrides: { root: { fontSize: 11, marginLeft: 0 } },
			},
			MuiSelect: {
				styleOverrides: { icon: { color: mono.dim } },
			},
			MuiMenu: {
				styleOverrides: {
					paper: {
						borderRadius: 2,
						border: `1px solid ${mono.line}`,
						boxShadow: '0 12px 32px rgba(20, 24, 32, 0.10)',
						marginTop: 4,
					},
					list: { padding: 4 },
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						fontSize: 13,
						minHeight: 34,
						borderRadius: 2,
						color: mono.ink,
						'&.Mui-selected': { background: 'rgba(22, 24, 27, 0.06)' },
					},
				},
			},
			MuiPopover: {
				styleOverrides: {
					paper: {
						borderRadius: 2,
						border: `1px solid ${mono.line}`,
						boxShadow: '0 12px 32px rgba(20, 24, 32, 0.10)',
					},
				},
			},
			MuiPaper: {
				defaultProps: { elevation: 0 },
				styleOverrides: {
					root: { backgroundImage: 'none' },
					rounded: { borderRadius: 2 },
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						borderRadius: 0,
						border: `1px solid ${mono.line}`,
						boxShadow: '0 30px 60px rgba(20, 24, 32, 0.12)',
					},
				},
			},
			MuiBackdrop: {
				styleOverrides: {
					root: {
						backgroundColor: 'rgba(22, 24, 27, 0.5)',
						'&.MuiBackdrop-invisible': { backgroundColor: 'transparent' },
					},
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						padding: '20px 24px 14px',
						fontSize: 16,
						fontWeight: 500,
						borderBottom: `1px solid ${mono.line}`,
					},
				},
			},
			MuiDialogContent: {
				styleOverrides: { root: { padding: '20px 24px' } },
			},
			MuiDialogActions: {
				styleOverrides: {
					root: {
						padding: '12px 24px 20px',
						borderTop: `1px solid ${mono.line}`,
						gap: 8,
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					indicator: { backgroundColor: mono.ink, height: 1 },
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						textTransform: 'none' as const,
						fontSize: 13,
						fontWeight: 500,
						color: mono.faint,
						minHeight: 40,
						'&.Mui-selected': { color: mono.ink },
					},
				},
			},
			MuiSwitch: {
				styleOverrides: {
					root: { padding: 8 },
					switchBase: {
						'&.Mui-checked': { color: mono.surface },
						'&.Mui-checked + .MuiSwitch-track': { backgroundColor: mono.ink, opacity: 1 },
					},
					thumb: { boxShadow: 'none', width: 16, height: 16, margin: 2 },
					track: { borderRadius: 999, backgroundColor: mono.lineSoft, opacity: 1 },
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						color: mono.lineInput,
						padding: 6,
						'&.Mui-checked': { color: mono.ink },
						'&.MuiCheckbox-indeterminate': { color: mono.ink },
						'& .MuiSvgIcon-root': { fontSize: 18 },
					},
				},
			},
			MuiRadio: {
				styleOverrides: {
					root: { color: mono.lineInput, '&.Mui-checked': { color: mono.ink } },
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						borderRadius: 2,
						fontSize: 11,
						letterSpacing: '.08em',
						height: 22,
						border: `1px solid ${mono.lineSoft}`,
						background: 'transparent',
						color: mono.dim,
					},
					filled: { background: mono.ink, borderColor: mono.ink, color: '#FFFFFF' },
					label: { padding: '0 8px' },
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						background: mono.ink,
						color: '#FFFFFF',
						fontSize: 11,
						fontWeight: 400,
						borderRadius: 2,
						padding: '6px 10px',
					},
					arrow: { color: mono.ink },
				},
			},
			MuiDivider: {
				styleOverrides: { root: { borderColor: mono.line } },
			},
			MuiListItemButton: {
				styleOverrides: {
					root: {
						borderRadius: 2,
						'&.Mui-selected': { background: 'rgba(22, 24, 27, 0.06)' },
						'&.Mui-selected:hover': { background: 'rgba(22, 24, 27, 0.08)' },
					},
				},
			},
			MuiPagination: {
				styleOverrides: { root: {} },
			},
			MuiPaginationItem: {
				styleOverrides: {
					root: {
						borderRadius: 2,
						fontSize: 12,
						minWidth: 28,
						height: 28,
						color: mono.dim,
						'&.Mui-selected': {
							background: mono.ink,
							color: '#FFFFFF',
							'&:hover': { background: mono.ctaBg },
						},
					},
				},
			},
			MuiTablePagination: {
				styleOverrides: {
					root: { color: mono.dim, fontSize: 12 },
					selectLabel: { fontSize: 12 },
					displayedRows: { fontSize: 12, fontVariantNumeric: 'tabular-nums' },
				},
			},
			MuiLinearProgress: {
				styleOverrides: {
					root: { backgroundColor: mono.line, height: 2 },
					bar: { backgroundColor: mono.ink },
				},
			},
			MuiCircularProgress: {
				styleOverrides: { root: { color: mono.ink } },
			},
			MuiSkeleton: {
				styleOverrides: { root: { backgroundColor: mono.line } },
			},
			MuiAlert: {
				styleOverrides: {
					root: {
						borderRadius: 0,
						border: `1px solid ${mono.line}`,
						background: mono.surface,
						color: mono.ink,
						fontSize: 13,
					},
					icon: { color: mono.dim },
				},
			},
			MuiAccordion: {
				defaultProps: { disableGutters: true, elevation: 0 },
				styleOverrides: {
					root: {
						border: 0,
						borderBottom: `1px solid ${mono.line}`,
						'&:before': { display: 'none' },
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: { borderRadius: 0, border: `1px solid ${mono.line}`, boxShadow: 'none' },
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						textTransform: 'none' as const,
						borderRadius: 2,
						borderColor: mono.lineSoft,
						color: mono.dim,
						fontSize: 12,
						'&.Mui-selected': {
							background: mono.ink,
							color: '#FFFFFF',
							'&:hover': { background: mono.ctaBg },
						},
					},
				},
			},
			MuiSlider: {
				styleOverrides: {
					root: { color: mono.ink, height: 2 },
					thumb: { width: 12, height: 12, boxShadow: 'none' },
					rail: { color: mono.lineSoft, opacity: 1 },
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: { border: `1px solid ${mono.line}`, borderRadius: 2 },
					option: { fontSize: 13 },
				},
			},
			MuiBadge: {
				styleOverrides: { badge: { borderRadius: 2, fontSize: 10 } },
			},
			MuiAvatar: {
				styleOverrides: { root: { fontFamily: boFont.ui } },
			},
		},
	}

	return language === 'ko'
		? createTheme(base, koKR, pickersKoKR, coreKoKR)
		: createTheme(base)
}

/**
 * 백오피스 트리를 감싼다. 안쪽 컴포넌트가 `theme.palette.primary` 를 읽으면 ink 가 나온다.
 *
 * ⚠ 테마를 **함수**로 넘긴다. 객체로 넘기면 MUI 가 상위 테마와 얕게 병합해서
 * App.tsx 의 CssVarsProvider 가 넣어 둔 `theme.vars` 가 그대로 남고, 컴포넌트는
 * `theme.vars.palette.primary.main` = `var(--mui-palette-primary-main)`(deepPurple)을 읽는다.
 * 함수 반환값은 병합 없이 그대로 쓰이므로 `vars` 가 없어 여기 hex 값이 나온다.
 */
export const BackofficeTheme = ({ children }: { children: React.ReactNode }) => {
	const language: string = useAppSelector(state => state.user.language)
	const theme = React.useMemo(() => buildTheme(language), [language])
	const replaceTheme = React.useCallback(() => theme, [theme])

	return <ThemeProvider theme={replaceTheme}>{children}</ThemeProvider>
}
